<template>
  <div :id="navId" class="article-tabbed-carousel" :class="Containerclasses">
    <div v-if="heading" class="heading-container">
      <div class="type">{{ type }}</div>
      <h1 class="heading">{{ heading }}</h1>
      <p class="heading-text">{{ headingText }}</p>
    </div>
    <div class="carousel" :class="classes">
      <div v-for="slide in slides" :key="slide.id" class="slide">
        <div class="image">
          <div v-if="slide.text" class="image__gradient">
            <Picture :images="slide.images" />
          </div>

          <Picture v-else :images="slide.images" />

          <div v-if="slide.credit" class="credit">{{ slide.credit }}</div>
          <Button
            v-if="slide.link"
            tag="a"
            :href="slide.link"
            target="_blank"
            class="linkButton"
          >
            {{ slide.linkText }}
          </Button>
        </div>
        <p v-if="slide.text" class="text">{{ slide.text }}</p>
        <Button
          v-if="slide.link2"
          tag="a"
          :href="slide.link2"
          target="_blank"
          class="linkButton2"
        >
          {{ slide.linkText2 }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import Picture from "@/components/Picture.vue";
import Button from "@/components/Button.vue";
export default {
  name: "ArticleTabbedCarousel",
  components: {
    Picture,
    Button,
  },
  props: {
    navId: {
      type: String,
      default: null,
    },
    heading: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    headingText: {
      type: String,
      default: null,
    },
    slides: {
      type: Array,
      required: true,
    },
    gap: {
      type: Boolean,
      default: false,
    },
    version2: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    Containerclasses() {
      return {
        "article-tabbed-carousel--version2": this.version2,
      };
    },
    classes() {
      return {
        "carousel--gap": this.gap,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.article-tabbed-carousel {
  margin: 0 auto 40px;

  @include respond-above(md) {
    margin: 0 auto 80px;
    max-width: 1600px;
  }
  &--version2 {
    .heading-container {
      text-align: left;
      .heading {
        font-size: 30px;
        margin: 0 0 20px;
        text-transform: none;
      }
      .heading-text {
        margin: 0 0 20px;
      }
    }
    .carousel {
      justify-content: left;
      .slide {
        background-color: #f1f1f1;
        padding: 0 0 150px;
        box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
      }
      .text {
        margin: 30px 0 10px 20px;
        font-size: 24px;
      }
      .linkButton2 {
        font-family: $heading-font-family;
        background: none;
        color: $color-grey-dark;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.55px;
        padding: 0;
        margin: 0 0 0 20px;
        text-decoration: underline;
        text-transform: none;
      }
    }
  }
}

.heading-container {
  grid-area: heading;
  padding: 0 20px;
  @include respond-above(md) {
    text-align: center;
  }
}

.heading {
  color: $color-grey-dark;
  margin: 0 20px 20px;
  @include t__editorial-headline-10;
  @include respond-above(md) {
    margin: 0 0 20px 0;
    @include t__editorial-headline-20;
  }
}

.heading-text {
  @include t__editorial-body-10;
  margin: 0 20px 20px;
  @include respond-above(md) {
    @include t__editorial-body-20;
  }
}

.text {
  color: $color-grey-dark;
  @include t__editorial-body-10;
  margin: 0 20px 10px;
  text-align: left;
  @include respond-above(md) {
    @include t__editorial-body-20;
    margin: 0;
  }
}

.type {
  color: $color-grey-dark;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.49px;
  margin: 0 0 10px 2px;
  text-transform: uppercase;

  @include respond-above(md) {
    font-size: 10px;
  }
}

.image {
  line-height: 0;
  margin: 0 0 10px 0;
  position: relative;

  &__gradient {
    &:after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      display: inline-block;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.5) 0%,
        rgba(0, 0, 0, 0) 30%
      );
    }
  }
}

.credit {
  color: $color-white;
  bottom: 15px;
  font-size: 9px;
  font-weight: 600;
  left: 15px;
  letter-spacing: 0.55px;

  position: absolute;

  @include respond-above(md) {
    bottom: 20px;
    font-size: 10px;
    left: 20px;
  }
}

.carousel {
  display: flex;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0 0 20px 0;

  @include respond-above(md) {
    justify-content: center;
  }

  & > * {
    flex: 1 0 87.5%;
    margin: 0;

    @include respond-above(md) {
      margin: 0 20px 0 0;
      max-width: 335px;
      flex: 1 0 40%;

      &:first-child {
        margin: 0 20px 0 20px;
      }
    }
  }

  &--gap {
    & > * {
      margin: 0 2px 0 0;
      @include respond-above(md) {
        margin: 0 20px 0 0;
      }
    }
  }
}

.linkButton {
  background: none;
  bottom: 15px;
  color: $color-white;
  font-size: 9px;
  font-weight: 600;
  left: 15px;
  letter-spacing: 0.55px;
  padding: 0;
  position: absolute;
  text-decoration: underline;

  @include respond-above(md) {
    bottom: 20px;
    font-size: 10px;
    left: 20px;
  }
}
</style>
